/// <reference path="Utilities/_reference.ts" />
/// <reference path="../../../node_modules/@types/leaflet/index.d.ts" />
/// <reference path="../../../node_modules/@types/geojson/index.d.ts" />

module YVR.TerminalMap {
	// Utilities
	import consts = YVR.Utilities.Map.consts;
	import events = YVR.Utilities.Events;
	import gtm = YVR.Utilities.Gtm;
	import urls = YVR.Utilities.Urls;
	import util = YVR.Utilities.Map;

	// Constants
	const ANALYTICS_CATEGORY = 'terminal-map';

	// Global state
	const cache: any = {};
	const templates = {
		popup: null
	};

	export const maps: TerminalMap[] = [];


	//#region INTERFACES

	interface ILayers {
		[key: string]: L.LayerGroup;
		elevators?: L.LayerGroup;
		escalators?: L.LayerGroup;
		locations?: L.LayerGroup;
	}

	//#endregion


	//#region Terminal Map Class

	class TerminalMap {
		config;
		container: JQuery;
		currentKeywordMaxLength = 1;
		element: JQuery;
		layers: ILayers = {};
		levels: { [key: number]: string };
		map: L.Map;
		state: util.IMapState;

		constructor(element: JQuery) {
			this.element = element;
			this.container = element.closest('.yvr-tab-content');

			this.config = $.extend({}, util.config);

			this.state = $.extend({}, util.config.initialState);

			this.initConfig();
			this.initMap();
			this.initState();
			this.renderFloorLevel();
			this.initEventBindings();
			this.initFloor();
		}

		initConfig() {
			this.levels = this.element.data('maps-levels');

			if (this.element.data('maps-center')) {
				this.config.center = this.element.data('maps-center');
			}

			if (this.element.data('maps-zoom-initial')) {
				this.config.zoom.initial = this.element.data('maps-zoom-initial');
			}

			if (this.element.data('maps-zoom-min')) {
				this.config.zoom.min = this.element.data('maps-zoom-min');
			}

			if (this.element.data('maps-zoom-max')) {
				this.config.zoom.max = this.element.data('maps-zoom-max');
			}

			if (this.element.data('maps-show-non-passenger-areas')) {
				this.config.showNonPassengerAreas = true;
			}
		}

		initMap() {
			const options: L.MapOptions = {
				attributionControl: false,
				center: L.latLng(this.config.center[0], this.config.center[1]),
				maxBounds: util.createBounds(this.config.center, this.config.radius),
				minZoom: this.config.zoom.min,
				maxZoom: this.config.zoom.max,
				zoom: this.config.zoom.initial,

				// Turn off the default zoom control.
				zoomControl: false
			};

			this.map = L.map(this.element.get(0), options);

			// Add a new zoom control with our customized position.
			this.map.addControl(new (L as any).Control.Zoom(this.config.zoomControl));
		}


		/**
		 * Check hash parameters to see if there is an initial facility.
		 */
		initState() {
			if (this.element.data('maps-state-level')) {
				this.state.level = this.element.data('maps-state-level');
			}

			if (this.element.data('maps-state-location')) {
				this.state.location = this.element.data('maps-state-location');
			}

			const facilityParameter = urls.getHashStringParameter('facility');
			const facility = parseInt(facilityParameter, 10);

			if (!isNaN(facility)) {
				this.state.facility = facility;
			}

			const locationParameter = urls.getHashStringParameter('locations');

			if (locationParameter) {
				this.state.location.push(locationParameter);
				let locationToggle = this.element.closest('.yvr-tab-content')
					.find('.yvr-form__toggle[name="locations-toggle"]');
				locationToggle.attr('aria-checked', 'false');
				locationToggle.filter(`[value="${locationParameter}"]`)
					.attr('checked', 'checked')
					.attr('aria-checked', 'true');
			}

			// Adds an "aria-current" status to the labels
			function addAriaCurrent() {

				const terminalLocation = document.getElementsByClassName('terminal-location');

				for (let i = 0; i < terminalLocation.length; i++) {

					terminalLocation[i].setAttribute('aria-current', 'false');
					terminalLocation[0].setAttribute('aria-current', 'true');

					terminalLocation[i].previousElementSibling.addEventListener('click', function () {

						for (let i = 0; i < terminalLocation.length; i++) {

							terminalLocation[i].setAttribute('aria-current', 'false');

						}

						if (terminalLocation[i].previousElementSibling == this) {

							terminalLocation[i].setAttribute('aria-current', 'true');

						}
					});



				}
			}

			addAriaCurrent();

		}

		/**
		 * Run a fake click on a the Leaflet layer control to select our default floor.
		 */
		initFloor() {
			setFloor(this.element, this.state.level);
		}


		/**
		 * Render a level of rooms from GeoJSON.
		 */
		renderFloorLevel() {
			const controlLayers = L.control.layers();
			const rooms = this.config.urls.rooms;

			this.map.addLayer(renderBaseLevelLayer(this.state, cache[rooms.BaseLayer], this.config));
			this.map.addLayer(renderParkingLevelLayer(cache[rooms.ParkingLots]));
			this.map.addLayer(renderRoadsLevelLayer(cache[rooms.Roads]));

			// Once all data is loaded, create each of the layers.
			for (let i = 1; i < Object.keys(this.levels).length + 1; i++) {
				let layer = renderFloorLevelLayer(this.config, this.state, cache[rooms[i]], i);

				// Add the layer as a base layer, so it can be switched using the Leaflet layer control.
				controlLayers.addBaseLayer(layer, this.levels[i]);
			}

			// Create a layer control with each level.
			this.map.addControl(controlLayers);

			renderElevatorLayer(this.map, this.state.level);
			//renderEscalatorLayer(this.map, this.state.level);

			renderGateLayer(this.map);
			renderGateAreasLayer(this.map);
		}


		/**
		 * Run through the steps to update the map markers on state changes.
		 */
		updateMap() {
			util.resetRoomStyle(this.map, this.state.terminals, this.config);

			for (const category in consts.SUBCATEGORIES) {
				if (consts.SUBCATEGORIES.hasOwnProperty(category)) {
					renderFacilityLayer(this.map, this.layers, this.state, this.config, consts.SUBCATEGORIES[category], consts.PROPERTIES.SUBCATEGORY);
				}
			}

			renderLocationLayer(this.map, this.layers, this.config, this.state, consts.PROPERTIES.CATEGORY, this.state.location);
		}



		//#region EVENT BINDINGS


		/**
		 * Initialise the event bindings for our custom refiner and level controls.
		 */
		initEventBindings() {
			this.onKeywordChange();
			this.onLevelChange();
			this.onLocationChange();
			this.onTerminalChange();
			this.onZoomChange();
		}


		/**
		 * Filter through the facility list with the current keyword.
		 */
		onKeywordChange() {
			// Get the new query from the input
			this.container.find('.yvr-form__input--keyword-search').on('input keyup', events.debounce(e => {
				const value = $(e.target).val().trim();

				this.state.keyword = value;
				renderLocationLayer(this.map, this.layers, this.config, this.state, consts.PROPERTIES.CATEGORY, this.state.location);

				if (value && value.length > this.currentKeywordMaxLength) {
					gtm.eventToDataLayer(ANALYTICS_CATEGORY, 'filter|keyword', value);

					this.currentKeywordMaxLength = value.length;
				}
			}, 400));

			this.container.find('.yvr-form__input-button').on('click', e => {
				e.preventDefault();

				const value = this.container.find('#keyword-search').val().trim();

				this.state.keyword = value;
			});
		}


		/**
		 * Changes the level shown on the map.
		 */
		onLevelChange() {
			// Attach to the `baselayerchange` event.
			this.map.on('baselayerchange', e => {
				$(this.map.getContainer()).find('.yvr-map-label__name').text((e as any).name);

				for (const name in this.levels) {
					if (this.levels.hasOwnProperty(name) && this.levels[name] === (e as any).name) {
						this.state.level = name;
					}
				}

				this.updateMap();

				gtm.eventToDataLayer(ANALYTICS_CATEGORY, 'filter|level', (e as any).name);
			});
		}


		/**
		 * Changes the locations on the map.
		 */
		onLocationChange(): void {
			const toggles = this.container.find('.yvr-form__toggle[name="locations-toggle"]');

			toggles.on('change', (e) => {
				const target = $(e.target);
				const value = target.val();
				let selector = 'label[for=' + target.attr('id') + ']';
				let label = $(selector);
				$('.yvr-form__toggle-label').attr('aria-checked', 'false');

				label.attr('aria-checked', 'true');
				if ((e.target as HTMLInputElement).checked) {
					// Show the keyword filter input
					this.container.find('.yvr-form__row--filter').show();

					// Uncheck the rest.
					toggles.not(e.target).prop('checked', false);
					this.state.location = [];

					// Save the location to an array, so potentially multiple locations can be on at once.
					this.state.location.push(value);

					gtm.eventToDataLayer(ANALYTICS_CATEGORY, 'filter|category', target.next().text().trim());
				} else {
					// Find the value in the location array to remove it.
					const index = this.state.location.indexOf(value);

					if (index > -1) {
						this.state.location.splice(index, 1);
					}

					// If the location array is empty
					if (!this.state.location.length) {
						// Hide the keyword filter input.
						this.container.find('.yvr-form__row--filter').hide();
					}
				}

				this.updateMap();
			});
		}


		/**
		 * Changes the selected terminal on the map. The terminal controls are simplified and map
		 * multiple terminals to one option. This also allows us to have both the US and International
		 * terminal options map to the same Sector, 'Swing'. Additionaly, we add both 'US' and 'USA' due
		 * to Sector and Terminal not using the same value.
		 */
		onTerminalChange(): void {
			this.container.find('.yvr-form__toggle[name="terminal-toggle"]').on('change', (e) => {
				this.state.terminals = $(e.target).val().split(';');
				let selector = 'label[for=' + $(e.target).attr('id') + ']';
				let label = $(selector);
				$('.terminal-location').attr('aria-checked', 'false');
				label.attr('aria-checked', 'true');
				this.updateMap();
				zoomIntoTerminal(this.map, this.config, this.state);

				gtm.eventToDataLayer(ANALYTICS_CATEGORY, 'filter|terminal', $(e.target).next().text().trim());
			});
		}


		/**
		 * Changes the room styles depending on the zoom level.
		 */
		onZoomChange(): void {
			this.map.on('zoomend', () => {
				util.resetRoomStyle(this.map, this.state.terminals, this.config);
			});
		}

		//#endregion

	}

	//#endregion


	//#region INIT MAP


	/**
	 * Initialise the entire mapping component.
	 */
	function init(): void {
		let elements = $('.yvr-map--terminal');

		if (elements.length) {
			initTabSwitcher();
			initTemplates();

			// Add `notranslate` classes to the map pane.
			elements.find('.leaflet-pane').addClass('notranslate');

			util.getFullMapData(cache)
				.then(initTerminalMap);
		}
	}


	/**
	 * Initialise the terminal map classes.
	 */
	export function initTerminalMap() {
		if (maps.length) {
			maps.forEach((value) => {
				value.map.remove();
			});
		}

		$('.yvr-map--terminal').each((index, element) => {
			maps.push(new TerminalMap($(element)));
		});
	}


	/**
	 * Initialise the tab switcher by hiding non-active content.
	 */
	function initTabSwitcher(): void {
		const buttons = $('.yvr-tab-switcher__button');
		const buttonActiveClassName = 'yvr-tab-switcher__button--active';
		const content = $('.yvr-tab-content');
		const contentHiddenClassName = 'yvr-tab-content--hidden';

		buttons.on('click', (e: JQueryEventObject) => {
			const button = $(e.target);
			const target = button.data('target');

			buttons.removeClass(buttonActiveClassName);
			button.addClass(buttonActiveClassName);

			content.removeClass(contentHiddenClassName)
				.not(target)
				.addClass(contentHiddenClassName);

			gtm.eventToDataLayer(ANALYTICS_CATEGORY, 'terminal-toggle', button.text().trim());
		});

		if (location.href === '#south-terminal' || urls.getHashStringParameter('terminal') === 'south-terminal') {
			buttons.filter('[data-target="#south-terminal"]').trigger('click');
		}
	}


	/**
	 * Initialises the popup template.
	 */
	function initTemplates(): void {
		//const popupSource = $('#yvr-map-popup-template').html();

		//if (popupSource) {
		//	templates.popup = Handlebars.compile(popupSource);
		//}

		if (document.documentElement.lang === "fr") {
			templates.popup = YVR.templates.terminalMapPopupFR;
		} else {
			templates.popup = YVR.templates.terminalMapPopupEN;
		}

	}


	/**
	 * Set the current floor by clicking on a Leaflet layer control.
	 */
	function setFloor(container: JQuery | HTMLElement, floor: string): void {
		const nthChild = parseInt(floor, 10) - 1;

		$(container).find(`.leaflet-control-layers-base input:nth(${nthChild})`).trigger('click');
	}


	//#endregion


	//#region LAYER HELPERS


	/**
	 * Create a GeoJSON layer using a set of default properties.
	 * @param data The GeoJSON data to create a layer with.
	 * @param props The properties to pass into the GeoJSON layer constructor.
	 */
	function createGeoJsonLayer(data: Object, props: Object): L.GeoJSON {
		const defaults = {
			interactive: false,
			fillOpacity: 1,
			keyboard: false,
			opacity: 1
		};

		const properties = $.extend({}, defaults, props);

		return (L as any).geoJSON(data, properties) as L.GeoJSON;
	}


	/**
	 * Create a Marker Cluster Group layer using a set of default properties;
	 * @param props The properties to pass into the marker cluster constructor.
	 */
	function createIconMarkerClusterGroup(props: Object): L.LayerGroup {
		const defaults = {
			showCoverageOnHover: false,
			spiderfyOnMaxZoom: false,
			zoomToBoundsOnClick: false
		};

		const properties = $.extend({}, defaults, props);

		return (L as any).markerClusterGroup(properties);
	}


	/**
	 * Match a property of a feature, removing the diacritics and lower casing.
	 * @param feature The properties of the GeoJSON feature.
	 * @param property The property to match.
	 * @param keyword The keyword to find within the property.
	 * @returns {boolean} Whether or not the feature matches.
	 */
	function propertyMatch(feature: { [key: string]: string }, property: string, keyword: string): boolean {
		return feature[property] &&
			feature[property].toLowerCase &&
			feature[property].toLowerCase().removeDiacritics().includes(keyword);
	}


	/**
	 * Filter location markers on the map based on facility IDs, keywords and current state.
	 * @param hasInitialFacility Whether or not the map as one initial feature selected.
	 * @param feature The properties of the GeoJSON feature.
	 * @param currentState The current state of the map.
	 * @param property The property to match.
	 * @param keyword The keyword to find within the property
	 */
	function filterFeature(hasInitialFacility: boolean, feature, currentState: util.IMapState, property: string, value: string | string[]) {
		const isOnMap = util.filterFeature(feature, currentState, property, value);
		const hasKeyword = currentState.keyword && currentState.keyword.length > 1;
		let isKeyword = false;

		if (currentState.keyword && currentState.keyword.length > 1) {
			const keyword = currentState.keyword.removeDiacritics().toLowerCase();

			isKeyword = propertyMatch(feature, util.consts.PROPERTIES.NAME, keyword) ||
				propertyMatch(feature, util.consts.PROPERTIES.SUBCATEGORY_DESCRIPTION, keyword) ||
				propertyMatch(feature, util.consts.PROPERTIES.DESCRIPTION, keyword) ||
				propertyMatch(feature, util.consts.PROPERTIES.KEYWORDS, keyword);
		}

		if (hasInitialFacility) {
			return !isKeyword && currentState.facility === feature.PoiID;
		} else {
			return (!hasKeyword && isOnMap) || (isKeyword && isOnMap);
		}
	}


	/**
	 * Add or remove layers based on map importance and current zoom.
	 * @param name The name prefix of the collection of layers to add or remove.
	 */
	function resetAllLayersOnImportance(map: L.Map, layers: { [key: string]: L.Layer }, name: string): void {
		map.on('zoomend', () => {
			let zoom = map.getZoom();
			zoom = Math.ceil(zoom);

			map.addLayer(layers[`${name}_15`]);

			if (zoom >= 16) {
				map.addLayer(layers[`${name}_16`]);
			} else {
				map.removeLayer(layers[`${name}_16`]);
			}

			if (zoom >= 17) {
				map.addLayer(layers[`${name}_17`]);
			} else {
				map.removeLayer(layers[`${name}_17`]);
			}

			if (zoom >= 18) {
				map.addLayer(layers[`${name}_18`]);
			} else {
				map.removeLayer(layers[`${name}_18`]);
			}
		});
	}


	/**
	 * Change the icon size of maps based on zoom level.
	 * @param name The name of the type of facility.
	 * @param markers The collection of facilities to change icon size of.
	 */
	function resetIconsOnZoom(map: L.Map, markers: L.Marker[]): void {
		map.on('zoomend', () => {
			let zoom = map.getZoom();
			const size = util.getSizeOnZoom(zoom);

			for (let marker of markers) {
				const icon = L.icon({
					className: (marker as any).options.icon.options.className,
					iconUrl: (marker as any).options.icon.options.iconUrl,
					iconSize: [size, size],
					iconAnchor: [size / 2, size / 2]
				});

				marker.setIcon(icon);
			}
		});
	}


	/**
	 * Zoom into a terminal by creating a new layer with just the active features.
	 */
	function zoomIntoTerminal(map, config, state) {
		// Create a layer to be able to zoom in on.
		const layer = L.geoJSON(cache[config.urls.rooms[state.level]], {
			filter(feature) {
				return state.terminals.includes((feature as any).properties.Sector);
			}
		});

		if (layer.getBounds().isValid()) {
			map.fitBounds(layer.getBounds(), config.fitBounds);
		}
	}


	//#endregion


	//#region RENDER LAYERS


	/**
	 * Adds a base layer of a level on our map with the given GeoJSON data.
	 * @param {Object} data             GeoJSON data.
	 */
	function renderBaseLevelLayer(state: util.IMapState, data: Object, config: any): L.GeoJSON {
		return createGeoJsonLayer(data, {
			style(feature) {
				return {
					color: util.getFeatureColor(feature, state.terminals, config)
				};
			}
		});
	}


	/**
	 * Adds a base parking lot layer of a level on our map with the given GeoJSON data.
	 * @param {Object} data             GeoJSON data.
	 */
	function renderParkingLevelLayer(data: Object): L.GeoJSON {
		return createGeoJsonLayer(data, {
			style(feature) {
				return {
					color: util.config.colors.road
				};
			}
		});
	}


	/**
	 * Adds a base layer of a level on our map with the given GeoJSON data.
	 * @param {Object} data             GeoJSON data.
	 */
	function renderRoadsLevelLayer(data: Object): L.GeoJSON {
		return createGeoJsonLayer(data, {
			style(feature) {
				return util.config.styles.roads;
			}
		});
	}


	/**
	 * Adds a base layer of a level on our map with the given GeoJSON data.
	 * @param {Object} data             GeoJSON data.
	 * @param {Number} floorLevelNumber The level number of the GeoJSON data.
	 */
	function renderFloorLevelLayer(config: any, state: util.IMapState, data: Object, floorLevelNumber: number): L.GeoJSON {
		return createGeoJsonLayer(data, {
			filter(feature) {
				// Filter out any sectors not necessary for the map, such as the parkade.
				return !config.excludedSectors.includes(feature.properties.Sector) &&
					floorLevelNumber === parseInt(feature.properties.FloorLevel, 10);
			},
			style(feature) {
				return {
					color: util.getFeatureColor(feature, state.terminals, config)
				};
			}
		});
	}


	/**
	 * Adds the facility icons to a map layer as a marker cluster. We are using marker clusters
	 * here to make pairs of washrooms appear to be just one washroom, as to not clutter the zoomed
	 * out views.
	 * @param {String} name     The name of the facility.
	 * @param {String} property The feature property to filter on.
	 */
	function renderFacilityLayer(map: L.Map, layers: { [key: string]: L.LayerGroup }, state: util.IMapState, config, name: string, property: string): void {
		util.removeLayer(map, layers[`${name}_15`]);
		util.removeLayer(map, layers[`${name}_16`]);
		util.removeLayer(map, layers[`${name}_17`]);
		util.removeLayer(map, layers[`${name}_18`]);

		// Create a facility icon to use for both the cluster group and individual icons.

		const markers: L.Marker[] = [];

		layers[`${name}_15`] = (L as any).layerGroup();
		layers[`${name}_16`] = (L as any).layerGroup();
		layers[`${name}_17`] = (L as any).layerGroup();
		layers[`${name}_18`] = (L as any).layerGroup();


		let zoom = map.getZoom();
		zoom = Math.ceil(zoom);
		const size = util.getSizeOnZoom(zoom);

		for (const feature of cache['locations']) {
			if (util.filterFeature(feature, state, property, name)) {
				const latlng = L.latLng(feature.Latitude, feature.Longitude);
				const icon = util.createFacilityIcon(name, size, feature);

				// Remove facility ID to not show page links for bathrooms, and baggage carousels
				if (name === util.consts.SUBCATEGORIES.BAGGAGE || name === util.consts.SUBCATEGORIES.WASHROOMS) {
					feature.FacilityId = null;
				}

				// SPECIAL CASE: Baggage carousels have their own icons.
				if (name === util.consts.SUBCATEGORIES.BAGGAGE && feature.PoiNameLocID) {
					const matches = feature.PoiNameLocID.match(util.consts.REGEX.CAROUSEL_NUMBER);
					feature.Picto = true;

					if (matches && matches.length && matches[1] > 0) {
						feature.StoreFront_4x3_Url = `${config.urls.icons}/baggage-carousels/${matches[1]}.svg`;
					} else {
						feature.StoreFront_4x3_Url = `${config.urls.icons}/${name}.svg`;
					}
				} else {
					feature.Picto = true;
					feature.StoreFront_4x3_Url = `${config.urls.icons}/${name}.svg`;
				}

				// Create a popup with our location data from the gateway.
				const html = createPopupContent(feature);
				const popup = L.popup(config.popup).setContent(html);

				const marker = L.marker(latlng, { icon })
					.bindPopup(popup);

				markers.push(marker);

				if (9 <= feature.PoiMapImportance && feature.PoiMapImportance <= 10) {
					// If importance is 9/10 show zoomed out
					layers[`${name}_15`].addLayer(marker);
				} else if (7 <= feature.PoiMapImportance && feature.PoiMapImportance <= 8) {
					// If importance is 7/8, show zoomed out
					layers[`${name}_16`].addLayer(marker);
				} else if (3 <= feature.PoiMapImportance && feature.PoiMapImportance <= 6) {
					// If importance is 3 - 6, show zoomed out
					layers[`${name}_17`].addLayer(marker);
				} else if (1 <= feature.PoiMapImportance && feature.PoiMapImportance <= 2) {
					layers[`${name}_18`].addLayer(marker);
				}
			}
		}

		if (zoom >= 15) {
			map.addLayer(layers[`${name}_15`]);
		}

		if (zoom >= 16) {
			map.addLayer(layers[`${name}_16`]);
		}

		if (zoom >= 17) {
			map.addLayer(layers[`${name}_17`]);
		}

		if (zoom >= 18) {
			map.addLayer(layers[`${name}_18`]);
		}

		// Hide and show layering based on zoom levels
		resetAllLayersOnImportance(map, layers, name);
		resetIconsOnZoom(map, markers);
	}



	/**
	 * Create a facility icon with the current zoom.
	 * @param {L.Map} map The current Leaflet map.
	 * @returns {L.Icon}
	 */
	function createFacilityIcon(map: L.Map, name: string): L.Icon {
		const zoom = map.getZoom();
		const size = util.getSizeOnZoom(zoom);

		return util.createFacilityIcon(name, size);
	}


	/**
	 * Adds an elevaltor of a level on our map with the given GeoJSON data.
	 * @param {L.Map} map The current Leaflet map.
	 */
	function renderElevatorLayer(map: L.Map, level: string): void {
		const name = 'elevator';

		// Cache markers
		const markers: L.Marker[] = [];

		// Create elevator icon
		const icon = createFacilityIcon(map, name);

		// Create a marker cluster group since elevators are common in group
		const layer = createIconMarkerClusterGroup({
			// Remove the disable of clustering since 19 is above our max zoom
			disableClusteringAtZoom: 19,
			// Use the elevator icon as our cluster icon`
			iconCreateFunction() {
				return createFacilityIcon(map, name);
			},
			// Use a smaller cluster radius than our facility clusters
			maxClusterRadius: 15
		});

		// Loop through the GeoJSON data for elevators
		const layerGroup = L.geoJSON(cache[util.config.urls.rooms.Elevators], {
			filter(geoJsonFeature: GeoJSON.Feature<GeoJSON.Point>) {
				const properties = geoJsonFeature.properties as util.IElevator;

				return properties.EquipmentF && properties.EquipmentF.includes(level);
			},
			pointToLayer(geoJsonPoint, latlng: L.LatLng) {
				const marker = L.marker(latlng, {
					icon,
					interactive: false,
					keyboard: false
				});

				markers.push(marker);

				return marker;
			}
		});

		layer.addLayer(layerGroup);

		util.resetOneLayerOnImportance(map, layer, 17);
		resetIconsOnZoom(map, markers);
	}


	/**
	 * Adds an escalator of a level on our map with the given GeoJSON data.
	 */
	//function renderEscalatorLayer(map: L.Map, level: string): void {
	//	// Create escalator icon
	//	const icon = createFacilityIcon('escalator');

	//	// Create a marker cluster group since elevators are common in group
	//	const layer = createIconMarkerClusterGroup({
	//		// Remove the disable of clustering since 19 is above our max zoom
	//		disableClusteringAtZoom: 19,
	//		// Use the elevator icon as our cluster icon
	//		iconCreateFunction() {
	//			return icon;
	//		},
	//		// Use a smaller cluster radius than our facility clusters
	//		maxClusterRadius: 15
	//	});

	//	const collection = cache[config.urls.rooms.Escalators] as GeoJSON.FeatureCollection<GeoJSON.LineString>;

	//	for (const feature of collection.features) {
	//		const properties = feature.properties as util.IEscalator;

	//		if (properties.EquipmentF && properties.EquipmentF.includes(state.level)) {
	//			const index = properties.EquipmentF.indexOf(state.level);
	//			const top = properties.EquipmentF.length - 1 === index;
	//			const descending = properties.descending === 'y';
	//			const type = descending ? '-descending' : '-ascending';

	//			let latlng: L.LatLng;

	//			if ((top && descending) ||
	//				(!top && !descending)) {
	//				latlng = L.latLng(feature.geometry.coordinates[1][1], feature.geometry.coordinates[1][0]);
	//			} else {
	//				latlng = L.latLng(feature.geometry.coordinates[0][1], feature.geometry.coordinates[0][0]);
	//			}

	//			const marker = L.marker(latlng, {
	//				icon: createFacilityIcon('escalator'),
	//				interactive: false,
	//				keyboard: false
	//			});

	//			marker.addTo(layer);
	//		}
	//	}

	//	// Hide and show layering based on zoom levels
	//	resetOneLayerOnImportance(map, layer, 17);
	//	resetIconsOnZoom(map, markers);
	//}


	/**
	 * Adds the gate labels to a map layer.
	 */
	function renderGateLayer(map: L.Map): void {
		// Create a marker cluster group since elevators are common in group
		const layer = createIconMarkerClusterGroup({
			disableClusteringAtZoom: 19,
			iconCreateFunction(cluster) {
				return util.createGateClusterIcon(cluster);
			},
			maxClusterRadius: util.config.clusters.maxClusterRadius
		});

		for (const feature of cache.gates) {
			const latlng = L.latLng(feature.Latitude, feature.Longitude);
			const icon = util.createGateIcon(feature);
			const marker = L.marker(latlng, {
				// Use a custom `divIcon` so we remove all styles except for the text
				icon,
				interactive: false,
				keyboard: false
			});

			layer.addLayer(marker);
		}

		util.resetOneLayerOnImportance(map, layer, 17);
	}


	/**
	 * Adds the gate labels to a map layer.
	 */
	function renderGateAreasLayer(map: L.Map): void {
		const markers: L.Marker[] = [];

		for (const feature of cache.gateAreas) {
			const latlng = L.latLng(feature.Latitude, feature.Longitude);
			const icon = util.createGateAreaIcon(feature);

			markers.push(L.marker(latlng, {
				// Use a custom `divIcon` so we remove all styles except for the text
				icon,
				interactive: false,
				keyboard: false
			}));
		}

		const layer = L.layerGroup(markers);
		map.addLayer(layer);
	}


	/**
	 * Adds the locations (dine, shop) to a map layer.
	 * @param {String} property The category or subcategory to filter locations on.
	 * @param {String} value    The value of the property above to run the comparison on.
	 */
	function renderLocationLayer(map: L.Map, layers: ILayers, config, state: util.IMapState, property: string, value: string[]): void {
		util.removeLayer(map, layers.locations);

		const clusterSize = 28;

		// Create a marker cluster to group together locations that are within close proximity of
		// each other.
		layers.locations = (L as any).markerClusterGroup($.extend({}, config.clusters, {
			// Updated the icon create function to allow us to use our simplified CSS style
			iconCreateFunction(cluster) {
				return L.divIcon({
					className: 'leaflet-marker-cluster',
					html: cluster.getChildCount(),
					iconSize: L.point(clusterSize, clusterSize)
				});
			}
		}));

		const hasInitialFacility = state.facility &&
			cache.locations.filter(feature => feature.PoiID === state.facility).length > 0;

		// Save the facility for zooming later
		let marker: L.Marker;
		let initialFeature;

		cache.locations
			.filter((feature) => filterFeature(hasInitialFacility, feature, state, property, value))
			.forEach((feature) => {
				const latlng = L.latLng(feature.Latitude, feature.Longitude);

				// Create a popup with our location data from the gateway.
				const html = createPopupContent(feature);
				const popup = L.popup(config.popup).setContent(html);

				initialFeature = feature;
				marker = L.marker(latlng, { icon: util.createIcon(false) })
					.bindPopup(popup)
					.on('popupopen', markerPopupOpen)
					.on('popupclose', markerPopupClose);

				layers.locations.addLayer(marker);
			});

		map.addLayer(layers.locations);

		if (hasInitialFacility && marker && marker.togglePopup) {
			// Set the current floor
			state.level = initialFeature.PoiBuildingLevel;
			setFloor(map.getContainer(), state.level);

			// Set the map center to the initial marker
			const latlng = marker.getLatLng();
			setTimeout(() => {
				map.setView(latlng, 17, {});
				marker.togglePopup();
			});

			// Reset the initial facility
			state.facility = null;
		}
	}


	//#endregion


	//#region POPUP CREATION AND EVENTS


	/**
	 * A callback function to run when a marker popup has been opened.
	 * This function will replace the icon with the selected icon.
	 * @param {Object} e Event object
	 */
	function markerPopupOpen(e: L.PopupEvent): void {
		const marker = (e.popup as any)._source;

		if (marker && marker.setIcon) {
			const selected = util.createIcon(true);

			marker.setIcon(selected);
		}
	}


	/**
	 * A callback function to run when a marker popup has been opened.
	 * This function will replace the icon with the unselected icon.
	 * @param {Object} e Event object
	 */
	function markerPopupClose(e: L.PopupEvent): void {
		const marker = (e.popup as any)._source;

		// Check to see if the marker exists. Then check to see if the
		// marker currently has an icon or not. If it does not have an
		// icon, this may be because the layer just changed. If the
		// layer has changed, this will cause an icon to appear across
		// all layers. Last, check to see that the `setIcon` method
		// exists.
		if (marker && marker._icon && marker.setIcon) {
			const selected = util.createIcon(false);

			marker.setIcon(selected);
		}
	}


	/**
	 * Creates the HTML content for a location popup.
	 * @param  {Object} properties A dictionary of all location properties.
	 * @return {String}            HTML content for a location popup.
	 */
	function createPopupContent(properties) {
		// Run the feature properties through our Handlebars template.
		return templates.popup(properties);
	}

	//#endregion




	function addCurrentAria() {

		const buttons = document.getElementsByClassName('yvr-tab-switcher__button');
		const attr = document.querySelectorAll('aria-current');

		buttons[0].setAttribute('aria-current', 'page');

		for (let i = 0; i < buttons.length; i++) {

			buttons[i].addEventListener('click', function () {

				if (this == buttons[0]) {

					buttons[0].setAttribute('aria-current', 'page');
					buttons[1].removeAttribute('aria-current');

				} else {

					buttons[1].setAttribute('aria-current', 'page');
					buttons[0].removeAttribute('aria-current');

				}

			});
		}
	}

	addCurrentAria();



	// Kick it
	init();
}
